import React, { useRef } from "react";
import { Form, Formik, FormikProps } from "formik";
import ActionSection from "features/terminal-details/common/actionSection";
import { toast } from "react-toastify";
import EquipmentFormFields from "./section/equipmentFormDetail";
import "./equipmentAddEditDetails.scss";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { Card } from "ui-components/card/card";
import { getEquipmentInfoValidation } from "../../../app/data/equipment/models";
import { useCreateEquipment, useUpdateEquipment } from "hooks/useEquipment";
import UserState from "slices/user/UserState";
import { UserUtils } from "app/data/user/userUtils";
import { useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";

interface EquipmentInfo {
    equipmentID: string;
    equipmentName: string;
    phone: string;
    email: string;
    streetAddress: string;
    city: string;
    stateProvince: string;
    zipPostalCode: string;
    country: string;
    scacCode: string;
    motorEquipmentNo: string;
    generalLiabilityDate: string;
    autoLiabilityDate: string;
    motorCargoDate?: string;
    workmanCompDate?: string;
    notes?: string;
    status: string;
}

const EquipmentAddEditDetails = ({
    onClose,
    initialEquipmentData,
    show,
    onTeamCreated
}: {
    onClose: () => void;
    initialEquipmentData?: EquipmentInfo;
    show: boolean;
    onTeamCreated?: () => void;
}): React.ReactElement => {

    const userState: UserState = useSelector(userSelector);
    const formRef = useRef<FormikProps<EquipmentInfo>>(null);

    const initialValues: EquipmentInfo = initialEquipmentData || {
        equipmentID: "(Auto-generated)",
        status: "",
        equipmentName: "",
        phone: "",
        email: "",
        streetAddress: "",
        city: "",
        stateProvince: "",
        zipPostalCode: "",
        country: "",
        scacCode: "",
        motorEquipmentNo: "",
        generalLiabilityDate: "",
        autoLiabilityDate: "",
        motorCargoDate: "",
        workmanCompDate: "",
        notes: "",
    };

    const { mutateAsync: createEquipment } = useCreateEquipment();
    const { mutateAsync: updateEquipment } = useUpdateEquipment();

    const isEdit = !!initialEquipmentData;

    const handleClear = () => {
        formRef.current?.resetForm();
    };

    const handleSubmit = async (values: EquipmentInfo) => {
        const payload = {
            equipmentName: values.equipmentName,
            status: values.status,
            phoneNumber: values.phone,
            equipmentEmail: values.email,
            equipmentAddress: values.streetAddress,
            city: values.city,
            stateProvince: values.stateProvince,
            zipPostalCode: values.zipPostalCode,
            country: values.country,
            scacCode: values.scacCode,
            mcNumber: values.motorEquipmentNo,
            generalLiabilityExpiryDate: values.generalLiabilityDate,
            autoLiabilityExpiryDate: values.autoLiabilityDate,
            motorCargoExpiryDate: values.motorCargoDate,
            workmanCompExpiryDate: values.workmanCompDate,
            notes: values.notes,
        };
        try {
            if (isEdit) {
                await updateEquipment({ id: Number(values.equipmentID), payload });
                toast.success("Equipment updated successfully");
            } else {
                await createEquipment(payload);
                toast.success("Equipment added successfully");
            }

            onClose();
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const mainHeaderTitle = isEdit
        ? (initialEquipmentData?.equipmentName ?? "Edit Equipment")
        : "Add New Equipment";

    return (
        <SlideOutSidebar
            show={show}
            header={mainHeaderTitle}
            onClose={onClose}
            size="small"
        >
            <Formik
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validationSchema={getEquipmentInfoValidation()}
                innerRef={formRef}
                enableReinitialize={true}
            >
                {(formikProps: FormikProps<EquipmentInfo>) => (
                    <Form>
                        {isEdit && (
                            <div className="equipment-view-container">
                                <div className="profile-header-equipment__title">
                                    <span className="profile-header-equipment__title-text">{"Equipment Information"}</span>
                                    {UserUtils.isXGSAdministrator(userState.profile) && (
                                        <div className="actions">
                                            <ActionSection
                                                handleClear={onClose}
                                                handleSave={formikProps.handleSubmit}
                                                spinner={false}
                                                disabled={!formikProps.dirty || !formikProps.isValid}
                                                primaryButtonText="Save Changes"
                                                secondaryButtonText="Cancel"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="equipment-view-container">
                            <EquipmentFormFields
                                formikProps={formikProps}
                                isEdit={isEdit}
                                CardComponent={Card}
                            />
                        </div>
                        {!isEdit && (
                            <div className="equipment-add-footer">
                                <ActionSection
                                    handleClear={handleClear}
                                    handleSave={formikProps.handleSubmit}
                                    spinner={false}
                                    disabled={!formikProps.dirty || !formikProps.isValid}
                                    primaryButtonText="Save Equipment"
                                    secondaryButtonText="Cancel"
                                />
                            </div>
                        )}

                    </Form>
                )
                }
            </Formik >
        </SlideOutSidebar >
    );
};

export default EquipmentAddEditDetails;
